import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categoryService } from '../services/api';
import ExportButton from '../components/ExportButton';
import './Categories.css';
import { Typography, Row, Col, Card, Button, Space, Tag, Table, Popconfirm, Switch, Tree, Tooltip } from 'antd';
import { PlusOutlined, ApartmentOutlined, UnorderedListOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
const { Title, Text } = Typography;

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'tree' ou 'list'

  useEffect(() => {
    loadCategories();
  }, [viewMode]);

  const loadCategories = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await categoryService.getCategories({ includeInactive: true, tree: viewMode === 'tree' });
      setCategories(response.categories);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      setError('Erreur lors du chargement des catégories');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (categoryId, categoryName) => {
    try {
      await categoryService.deleteCategory(categoryId);
      loadCategories();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression de la catégorie');
    }
  };

  const toggleActive = async (category) => {
    try {
      await categoryService.updateCategory(category.id, { isActive: !category.isActive });
      loadCategories();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour de la catégorie');
    }
  };

  const toTreeData = (nodes = []) => nodes.map((cat) => ({
    key: cat.id,
    title: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {cat.image ? (
          <img src={`${cat.image}`} alt={cat.name} style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 6 }} />
        ) : (
          <div style={{ width: 28, height: 28, background: '#f5f5f5', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-tag"></i>
          </div>
        )}
        <span style={{ fontWeight: 500 }}>{cat.name}</span>
        <Tag style={{ marginLeft: 4, background: '#fafafa', borderColor: '#f0f0f0' }}>/{cat.slug}</Tag>
        <Tag color={cat.isActive ? 'green' : 'default'}>{cat.isActive ? 'Active' : 'Inactive'}</Tag>
        <Space size={4} style={{ marginLeft: 'auto' }}>
          <Tooltip title="Voir">
            <Link to={`/admin/categories/${cat.id}`}><Button type="text" size="small" icon={<EyeOutlined />} /></Link>
          </Tooltip>
          <Tooltip title="Modifier">
            <Link to={`/admin/categories/${cat.id}/edit`}><Button type="text" size="small" icon={<EditOutlined />} /></Link>
          </Tooltip>
          <Switch checked={cat.isActive} size="small" onChange={() => toggleActive(cat)} />
          <Popconfirm title={`Supprimer ${cat.name} ?`} onConfirm={() => handleDelete(cat.id, cat.name)}>
            <Button type="text" danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      </div>
    ),
    children: cat.subcategories ? toTreeData(cat.subcategories) : undefined
  }));

  const columns = [
    {
      title: 'Catégorie',
      dataIndex: 'name',
      render: (_, cat) => (
        <Space align="center">
          {cat.image ? (
            <img src={`${cat.image}`} alt={cat.name} style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 6 }} />
          ) : (
            <div style={{ width: 32, height: 32, background: '#f5f5f5', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-tag"></i>
            </div>
          )}
          <div>
            <div style={{ fontWeight: 500 }}>{cat.name}</div>
            <Text type="secondary">/{cat.slug}</Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Statut',
      dataIndex: 'isActive',
      render: (_, cat) => (
        <Tag color={cat.isActive ? 'green' : 'default'}>{cat.isActive ? 'Active' : 'Inactive'}</Tag>
      )
    },
    {
      title: 'Produits',
      dataIndex: 'productCount',
      align: 'right',
      render: (v) => <span className="product-count">{v || 0}</span>
    },
    {
      title: 'Actions',
      width: 160,
      render: (_, cat) => (
        <Space>
          <Tooltip title="Voir">
            <Link to={`/admin/categories/${cat.id}`}><Button type="text" icon={<EyeOutlined />} /></Link>
          </Tooltip>
          <Tooltip title="Modifier">
            <Link to={`/admin/categories/${cat.id}/edit`}><Button type="text" icon={<EditOutlined />} /></Link>
          </Tooltip>
          <Switch checked={cat.isActive} size="small" onChange={() => toggleActive(cat)} />
          <Popconfirm title={`Supprimer ${cat.name} ?`} onConfirm={() => handleDelete(cat.id, cat.name)}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  if (isLoading) {
    return (
      <div className="categories-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Chargement des catégories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="categories-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="page-title">
          <Title level={3} style={{ marginBottom: 4 }}>Gestion des Catégories</Title>
          <Text type="secondary">Organisez vos produits par catégories</Text>
        </div>
        <Space>
          <Button type="primary" style={{backgroundColor:'#002c3e'}}>
            <Link to="/admin/categories/new"><PlusOutlined /> Nouvelle catégorie</Link>
          </Button>
        </Space>
      </div>

      {error && (
        <Card bordered={false} style={{ marginBottom: 16 }}>
          <Text type="danger">{error}</Text>
        </Card>
      )}

      {viewMode === 'tree' ? (
        <Card bordered={false}>
          <Tree showLine defaultExpandAll treeData={toTreeData(categories)} />
        </Card>
      ) : (
        <Card bordered={false}>
          <Table rowKey="id" dataSource={categories} columns={columns} pagination={false} size="small" />
        </Card>
      )}
    </div>
  );
};

export default Categories;
